import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './FilterSearchBar.module.css';
import { useFilterOptions } from '../../Analyse/CompactFilters/useFilterOptions';
import type { FilterConfig, ISelectOption } from '../../Analyse/CompactFilters/filterTypes';
import {
  dateRangeValue,
  makeDateRangeOption,
  formatDateRangeLabel,
} from '../../Analyse/CompactFilters/filterUtils';

type FilterSearchBarProps = {
  value: string;
  onChange: (value: string) => void;
  filters: FilterConfig[];
  selectedFilters: Record<string, ISelectOption[]>;
  onFilterChange: (filters: Record<string, ISelectOption[]>) => void;
  placeholder?: string;
};

type Chip = {
  filterKey: string;
  filterLabel: string;
  option: ISelectOption;
};

const fuzzyMatch = (haystack: string, needle: string): boolean => {
  if (!needle) return true;
  return haystack.toLowerCase().includes(needle.toLowerCase());
};

const FilterSearchBar: React.FC<FilterSearchBarProps> = ({
  value,
  onChange,
  filters,
  selectedFilters,
  onFilterChange,
  placeholder = 'Search...',
}) => {
  const filterOptions = useFilterOptions(filters);

  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [pendingFilterKey, setPendingFilterKey] = useState<string | null>(null);
  const [pendingQuery, setPendingQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const pendingInputRef = useRef<HTMLInputElement | null>(null);
  const pendingDateRef = useRef<HTMLInputElement | null>(null);

  const pendingFilter = useMemo(
    () => filters.find((f) => f.key === pendingFilterKey) || null,
    [filters, pendingFilterKey],
  );

  const isPendingDateRange = pendingFilter?.kind === 'dateRange';

  const chips = useMemo<Chip[]>(() => {
    const out: Chip[] = [];
    for (const filter of filters) {
      const selected = selectedFilters[filter.key] || [];
      for (const option of selected) {
        out.push({ filterKey: filter.key, filterLabel: filter.label, option });
      }
    }
    return out;
  }, [filters, selectedFilters]);

  // Date filters have no options list, so they must bypass the emptiness guard.
  const availableFilters = useMemo(
    () => filters.filter((f) => f.kind === 'dateRange' || (filterOptions[f.key] || []).length > 0),
    [filters, filterOptions],
  );

  const pendingOptionMatches = useMemo(() => {
    if (!pendingFilter || pendingFilter.kind === 'dateRange') return [];
    const all = filterOptions[pendingFilter.key] || [];
    if (!pendingQuery) return all;
    return all.filter((o) => fuzzyMatch(o.label, pendingQuery));
  }, [pendingFilter, filterOptions, pendingQuery]);

  const totalStage1Rows = availableFilters.length + (value ? 1 : 0);
  const totalRows = pendingFilter ? pendingOptionMatches.length : totalStage1Rows;

  useEffect(() => {
    if (activeIndex >= totalRows) setActiveIndex(0);
  }, [totalRows, activeIndex]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
        setPendingFilterKey(null);
        setPendingQuery('');
        setFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const isOptionSelected = (filterKey: string, optionValue: string): boolean =>
    (selectedFilters[filterKey] || []).some((o) => o.value === optionValue);

  const toggleOption = useCallback(
    (filterKey: string, option: ISelectOption) => {
      const current = selectedFilters[filterKey] || [];
      const exists = current.some((o) => o.value === option.value);
      const next = exists
        ? current.filter((o) => o.value !== option.value)
        : [...current, option];
      onFilterChange({ ...selectedFilters, [filterKey]: next });
    },
    [selectedFilters, onFilterChange],
  );

  const removeChip = (chip: Chip) => {
    const current = selectedFilters[chip.filterKey] || [];
    onFilterChange({
      ...selectedFilters,
      [chip.filterKey]: current.filter((o) => o.value !== chip.option.value),
    });
  };

  const beginPendingFilter = (filterKey: string) => {
    const target = filters.find((f) => f.key === filterKey);
    setPendingFilterKey(filterKey);
    setPendingQuery('');
    setActiveIndex(0);
    setOpen(true);
    setTimeout(() => {
      if (target?.kind === 'dateRange') pendingDateRef.current?.focus();
      else pendingInputRef.current?.focus();
    }, 0);
  };

  /**
   * Date ranges update live rather than committing on change: the user needs to
   * set both ends, so closing the pending filter after the first pick would be
   * wrong. cancelPending (the Done button, Escape or a click away) closes it.
   */
  const setPendingDateEnd = (end: 'from' | 'to', raw: string) => {
    if (!pendingFilter) return;
    const current = dateRangeValue(selectedFilters[pendingFilter.key]);
    const next = { ...current, [end]: raw };
    onFilterChange({
      ...selectedFilters,
      [pendingFilter.key]: makeDateRangeOption(next.from, next.to),
    });
  };

  const commitPendingOption = (option: ISelectOption) => {
    if (!pendingFilter) return;
    toggleOption(pendingFilter.key, option);
    setPendingFilterKey(null);
    setPendingQuery('');
    setActiveIndex(0);
    inputRef.current?.focus();
  };

  const cancelPending = () => {
    setPendingFilterKey(null);
    setPendingQuery('');
    setActiveIndex(0);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const isPendingInput = e.currentTarget === pendingInputRef.current;
    const currentInputValue = isPendingInput ? pendingQuery : value;

    if (e.key === 'Escape') {
      if (pendingFilter) cancelPending();
      else setOpen(false);
      return;
    }

    // The native date input owns its own keyboard handling (arrows step the
    // segments, Backspace clears them), so leave everything but Escape to it.
    if (isPendingDateRange) return;

    if (e.key === 'Backspace' && currentInputValue === '') {
      if (isPendingInput) {
        e.preventDefault();
        cancelPending();
        return;
      }
      if (chips.length > 0) {
        e.preventDefault();
        removeChip(chips[chips.length - 1]);
        return;
      }
    }

    if (!open) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (totalRows > 0 ? (i + 1) % totalRows : 0));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (totalRows > 0 ? (i - 1 + totalRows) % totalRows : 0));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (pendingFilter) {
        const option = pendingOptionMatches[activeIndex];
        if (option) commitPendingOption(option);
        return;
      }
      if (activeIndex < availableFilters.length) {
        beginPendingFilter(availableFilters[activeIndex].key);
      } else {
        setOpen(false);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
    setActiveIndex(0);
    if (!open) setOpen(true);
  };

  const handlePendingInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPendingQuery(e.target.value);
    setActiveIndex(0);
    if (!open) setOpen(true);
  };

  const handleContainerClick = () => {
    if (pendingFilter && isPendingDateRange) pendingDateRef.current?.focus();
    else if (pendingFilter) pendingInputRef.current?.focus();
    else inputRef.current?.focus();
  };

  return (
    <div ref={containerRef} className={styles.container} onClick={handleContainerClick}>
      <div className={`${styles.inputContainer} ${focused ? styles.focused : ''} search-bar`}>
        <span className={styles.icon}>
          <img src="/img/search-icon.svg" alt="Search Icon" />
        </span>

        {chips.map((chip, idx) => (
          <span key={`${chip.filterKey}-${chip.option.value}-${idx}`} className={styles.chip}>
            <span className={styles.chipLabel}>{chip.filterLabel}</span>
            <span className={styles.chipSeparator}>:</span>
            <span className={styles.chipValue}>{chip.option.label}</span>
            <button
              type="button"
              className={styles.chipRemove}
              onClick={(e) => {
                e.stopPropagation();
                removeChip(chip);
              }}
              aria-label={`Remove ${chip.filterLabel} ${chip.option.label}`}
            >
              ×
            </button>
          </span>
        ))}

        {pendingFilter && (
          <span className={styles.pendingChip}>
            <span className={styles.pendingChipLabel}>{pendingFilter.label}:</span>
            {isPendingDateRange ? (
              // The two inputs live in the dropdown; the chip just shows the
              // current range so it stays legible while both ends are picked.
              <span className={styles.pendingDateSummary}>
                {formatDateRangeLabel(
                  dateRangeValue(selectedFilters[pendingFilter.key]).from,
                  dateRangeValue(selectedFilters[pendingFilter.key]).to
                ) || 'any date'}
              </span>
            ) : (
              <input
                ref={pendingInputRef}
                className={styles.pendingInput}
                value={pendingQuery}
                placeholder={`Search ${pendingFilter.label.toLowerCase()}…`}
                onFocus={() => {
                  setFocused(true);
                  setOpen(true);
                }}
                onBlur={() => setFocused(false)}
                onChange={handlePendingInputChange}
                onKeyDown={handleKeyDown}
                onClick={(e) => e.stopPropagation()}
              />
            )}
          </span>
        )}

        <input
          ref={inputRef}
          className={styles.input}
          value={value}
          placeholder={chips.length === 0 && !pendingFilter ? placeholder : ''}
          onFocus={() => {
            setFocused(true);
            setOpen(true);
          }}
          onBlur={() => setFocused(false)}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
        />
      </div>

      {open && filters.length > 0 && (
        <div className={styles.dropdown}>
          {pendingFilter && isPendingDateRange ? (
            <>
              <div className={styles.dropdownHeader}>{pendingFilter.placeholder}</div>
              <div className={styles.dateRangeFields}>
                <label className={styles.dateField}>
                  <span className={styles.dateFieldLabel}>From</span>
                  <input
                    ref={pendingDateRef}
                    type="date"
                    lang="en-GB"
                    className={styles.dateInput}
                    max={dateRangeValue(selectedFilters[pendingFilter.key]).to || undefined}
                    value={dateRangeValue(selectedFilters[pendingFilter.key]).from}
                    onChange={(e) => setPendingDateEnd('from', e.target.value)}
                    onKeyDown={handleKeyDown}
                    onClick={(e) => e.stopPropagation()}
                  />
                </label>
                <label className={styles.dateField}>
                  <span className={styles.dateFieldLabel}>To</span>
                  <input
                    type="date"
                    lang="en-GB"
                    className={styles.dateInput}
                    min={dateRangeValue(selectedFilters[pendingFilter.key]).from || undefined}
                    value={dateRangeValue(selectedFilters[pendingFilter.key]).to}
                    onChange={(e) => setPendingDateEnd('to', e.target.value)}
                    onKeyDown={handleKeyDown}
                    onClick={(e) => e.stopPropagation()}
                  />
                </label>
                <button
                  type="button"
                  className={styles.dateDoneButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    cancelPending();
                  }}
                >
                  Done
                </button>
              </div>
            </>
          ) : pendingFilter ? (
            <>
              <div className={styles.dropdownHeader}>{pendingFilter.placeholder}</div>
              <div className={styles.optionsList}>
                {pendingOptionMatches.length === 0 ? (
                  <div className={styles.empty}>No matches.</div>
                ) : (
                  pendingOptionMatches.map((option, idx) => {
                    const selected = isOptionSelected(pendingFilter.key, option.value);
                    return (
                      <button
                        type="button"
                        key={option.value}
                        className={`${styles.optionItem} ${selected ? styles.selected : ''} ${idx === activeIndex ? styles.active : ''}`}
                        onMouseEnter={() => setActiveIndex(idx)}
                        onClick={(e) => {
                          e.stopPropagation();
                          commitPendingOption(option);
                        }}
                      >
                        <span className={styles.optionPrimary}>{option.label}</span>
                        {selected && <span className={styles.optionSecondary}>Added</span>}
                      </button>
                    );
                  })
                )}
              </div>
            </>
          ) : (
            <>
              <div className={styles.dropdownHeader}>Filter by…</div>
              <div className={styles.optionsList}>
                {availableFilters.length === 0 ? (
                  <div className={styles.empty}>No matching filters.</div>
                ) : (
                  availableFilters.map((filter, idx) => (
                    <button
                      type="button"
                      key={filter.key}
                      className={`${styles.optionItem} ${idx === activeIndex ? styles.active : ''}`}
                      onMouseEnter={() => setActiveIndex(idx)}
                      onClick={(e) => {
                        e.stopPropagation();
                        beginPendingFilter(filter.key);
                      }}
                    >
                      <span className={styles.optionPrimary}>{filter.label}</span>
                      <span className={styles.optionSecondary}>{filter.placeholder}</span>
                    </button>
                  ))
                )}
              </div>
              {value && (
                <>
                  <div className={styles.divider} />
                  <button
                    type="button"
                    className={`${styles.searchRow} ${activeIndex === availableFilters.length ? styles.active : ''}`}
                    onMouseEnter={() => setActiveIndex(availableFilters.length)}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpen(false);
                    }}
                  >
                    <span className={styles.searchRowLabel}>Search for</span>
                    <span className={styles.searchRowQuery}>"{value}"</span>
                  </button>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default FilterSearchBar;
